/**
 * What the composer lets a rep attach to a text (ADR-140 in `platform`), and what it says when it
 * will not. Pure, for `test/mms.test.ts`.
 *
 * Only a first check, to spare an upload for a file that could never be sent. core-api checks the
 * type and the size again on the media route, and has the last word. The numbers here mirror its
 * side of ADR-140.
 *
 * The non-obvious invariant: the 5 MB cap is Twilio's limit on a message AND ALL ITS MEDIA
 * COMBINED, not a cap per file. So `mmsFileProblem` is asked about a file *plus* the bytes already
 * attached — four 2 MB photos are each fine alone and together are a send that fails after every
 * one of them has been uploaded.
 */

export const MMS_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/bmp",
  "image/tiff",
  "application/pdf",
] as const;

/** For the file input's `accept`, so the chooser offers only these. */
export const MMS_ACCEPT = MMS_TYPES.join(",");

/** Twilio's cap on a message and all its media together. */
export const MMS_TOTAL_MAX_BYTES = 5 * 1024 * 1024;

/** No single file can be larger than the whole message may be, so the two caps are the same
 *  number. Named separately because they are two different rules, and only one of them moves if
 *  the provider ever raises the combined limit. */
export const MMS_FILE_MAX_BYTES = MMS_TOTAL_MAX_BYTES;

/** core-api's own cap. An eleventh is a 422 from the send schema, not a refusal with a sentence to
 *  show, so the strip must stop it here rather than let the send explain it. */
export const MMS_MAX_FILES = 10;

/** "4.2 MB": a byte count as the strip says it. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why this file cannot be attached, or `null` to upload it.
 *
 * `alreadyAttachedBytes` is the total of what is on the strip already: a file that fits on its own
 * is still refused when it would push the message past the combined cap.
 */
export function mmsFileProblem(
  file: { type: string; size: number },
  alreadyAttachedBytes = 0,
): string | null {
  if (file.size === 0) return "The file is empty. Choose another.";
  if (!(MMS_TYPES as readonly string[]).includes(file.type)) {
    return "Choose a picture (JPEG, PNG, GIF, WebP, HEIC, BMP or TIFF) or a PDF.";
  }
  if (file.size > MMS_FILE_MAX_BYTES) {
    return `This file is too large. A text and its files can be ${formatBytes(MMS_TOTAL_MAX_BYTES)} altogether.`;
  }
  if (alreadyAttachedBytes + file.size > MMS_TOTAL_MAX_BYTES) {
    return `Together these files are over ${formatBytes(MMS_TOTAL_MAX_BYTES)}, which is all a text can carry. Remove one first.`;
  }
  return null;
}
